// SalesLeadDashboard.jsx
import React, { Component } from "react";
import CartContext from "./CartContext";
import {
  Modal,
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Spinner,
} from "react-bootstrap";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
} from "chart.js";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import axios from "axios";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement
);

const STATUS_LABELS = { 1: "Open", 2: "Followup", 3: "Closed", 4: "Cancelled" };
const TYPE_LABELS = { 1: "Cold", 2: "Warm", 3: "Hot" };

// --- helpers ---
const formatDateISO = (date) => date.toISOString().split("T")[0];
const formatDateShort = (dateStr) =>
  new Date(dateStr).toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
  });

function getMonthStartEnd() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return [formatDateISO(start), formatDateISO(end)];
}
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
function getTodayStartEnd() {
  const now = new Date();
  return [formatDateISO(startOfDay(now)), formatDateISO(endOfDay(now))];
}
function getWeekStartEnd() {
  const now = new Date();
  const dow = now.getDay(); // 0=Sun ... 6=Sat
  const daysFromMon = (dow + 6) % 7; // Mon=0
  const monday = new Date(now);
  monday.setDate(now.getDate() - daysFromMon);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return [formatDateISO(startOfDay(monday)), formatDateISO(endOfDay(sunday))];
}
function getYearStartEnd() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  const end = new Date(now.getFullYear(), 11, 31);
  return [formatDateISO(start), formatDateISO(end)];
}
// safe pick to handle camelCase or PascalCase from API
const pick = (obj, ...names) => {
  for (const n of names)
    if (obj && Object.prototype.hasOwnProperty.call(obj, n)) return obj[n];
  return undefined;
};
// ----------------

export default class SalesLeadDashboard extends Component {
  static contextType = CartContext;

  state = {
    params: {
      storeId: "",
      assignedTo: "",
      from: getMonthStartEnd()[0],
      to: getMonthStartEnd()[1],
    },
    dateMode: "month", // "today" | "week" | "month" | "year" | "custom"
    summary: null,
    loading: false,
    error: null,
  };

  componentDidMount() {
    this.applyPreset("month");
    window.addEventListener("resize", this.handleResize);
  }
  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }
  handleResize = () => this.forceUpdate();

  fetchSummary = async () => {
    this.setState({ loading: true, error: null });
    try {
      const { assignedTo, from, to } = this.state.params;
      const res = await axios.get(
        (process.env.REACT_APP_API || "/api/") +
          "salesleads/summary/" +
          this.context.storeId,
        {
          params: {
            storeId: this.context.storeId || undefined,
            assignedTo: assignedTo || undefined,
            from,
            to,
          },
        }
      );
      this.setState({ summary: res.data, loading: false });
    } catch (err) {
      this.setState({
        error: err.response?.data?.message || err.message,
        loading: false,
      });
    }
  };

  // ---------- Filters ----------
  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState(
      (s) => ({ params: { ...s.params, [name]: value } }),
      () => this.state.dateMode === "custom" && this.fetchSummary()
    );
  };

  applyPreset = (mode) => {
    let from, to;
    switch (mode) {
      case "today":
        [from, to] = getTodayStartEnd();
        break;
      case "week":
        [from, to] = getWeekStartEnd();
        break;
      case "month":
        [from, to] = getMonthStartEnd();
        break;
      case "year":
        [from, to] = getYearStartEnd();
        break;
      case "custom":
      default:
        // keep current from/to for custom
        this.setState({ dateMode: "custom" }, this.fetchSummary);
        return;
    }
    this.setState(
      (s) => ({ dateMode: mode, params: { ...s.params, from, to } }),
      this.fetchSummary
    );
  };

  renderQuickFilterButtons = () => {
    const { dateMode } = this.state;
    const Btn = ({ id, children }) => (
      <Button
        size="sm"
        variant={dateMode === id ? "primary" : "outline-primary"}
        onClick={() => this.applyPreset(id)}
      >
        {children}
      </Button>
    );
    return (
      <div className="d-flex flex-wrap gap-2">
        <Btn id="today">Today</Btn>
        <Btn id="week">This Week</Btn>
        <Btn id="month">This Month</Btn>
        <Btn id="year">This Year</Btn>
        <Btn id="custom">Custom</Btn>
      </div>
    );
  };

  renderQuickFilterDropdown = () => {
    const { dateMode } = this.state;
    return (
      <Form.Select
        size="sm"
        aria-label="Quick date filters"
        value={dateMode}
        onChange={(e) => this.applyPreset(e.target.value)}
      >
        <option value="today">Today</option>
        <option value="week">This Week</option>
        <option value="month">This Month</option>
        <option value="year">This Year</option>
        <option value="custom">Custom</option>
      </Form.Select>
    );
  };

  renderQuickFilters() {
    const { dateMode } = this.state;
    const Btn = ({ id, children }) => (
      <Button
        size="sm"
        variant={dateMode === id ? "primary" : "outline-primary"}
        onClick={() => this.applyPreset(id)}
      >
        {children}
      </Button>
    );
    return (
      <div className="d-flex flex-wrap gap-2 mb-2">
        <Btn id="today">Today</Btn>
        <Btn id="week">This Week</Btn>
        <Btn id="month">This Month</Btn>
        <Btn id="year">This Year</Btn>
        <Btn id="custom">Custom</Btn>
      </div>
    );
  }

  renderFilters() {
    const { from, to } = this.state.params;
    const isCustom = this.state.dateMode === "custom";

    return (
      <Card className="shadow-sm rounded-3 mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end flex-wrap">
            {/* Buttons on md+ */}
            <Col md="auto" className="d-none d-md-flex">
              {this.renderQuickFilterButtons()}
            </Col>

            {/* Dropdown on sm-and-down */}
            <Col xs={12} className="d-flex d-md-none">
              {this.renderQuickFilterDropdown()}
            </Col>

            {/* Custom date inputs only when Custom is selected */}
            {isCustom && (
              <>
                <Col xs={6} md={2}>
                  <Form.Label>From</Form.Label>
                  <Form.Control
                    type="date"
                    name="from"
                    value={from}
                    onChange={this.handleChange}
                    autoFocus
                  />
                </Col>
                <Col xs={6} md={2}>
                  <Form.Label>To</Form.Label>
                  <Form.Control
                    type="date"
                    name="to"
                    value={to}
                    onChange={this.handleChange}
                  />
                </Col>
              </>
            )}

            {/* Refresh button */}
            <Col md="auto" className="d-grid ms-auto">
              <Button onClick={this.fetchSummary} disabled={this.state.loading}>
                {this.state.loading ? <Spinner size="sm" /> : "Refresh"}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    );
  }

  // ---------- UI blocks ----------
  renderKpis() {
    const s = this.state.summary || {};
    const t = pick(s, "Totals", "totals") || {};
    const kpi = [
      { label: "All Leads", value: t.All ?? t.all ?? 0 },
      { label: "Open", value: t.Open ?? t.open ?? 0 },
      { label: "Followup", value: t.Followup ?? t.followup ?? 0 },
      { label: "Closed", value: t.Closed ?? t.closed ?? 0 },
      { label: "Cancelled", value: t.Cancelled ?? t.cancelled ?? 0 },
    ];
    return (
      <Row className="g-3">
        {kpi.map((x, i) => (
          <Col key={i} lg={2} md={4} sm={6}>
            <Card className="shadow-sm rounded-3 h-100">
              <Card.Body className="d-flex flex-column justify-content-center">
                <div className="fw-semibold text-muted">{x.label}</div>
                <div className="display-6">{x.value}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  }

  renderCharts() {
    const s = this.state.summary || {};

    const byStatusArr = (pick(s, "ByStatus", "byStatus") || [])
      .slice()
      .sort((a, b) => (a.Key ?? a.key) - (b.Key ?? b.key));
    const statusBarData = {
      labels: byStatusArr.map(
        (x) => (x.Name ?? x.name) || STATUS_LABELS[x.Key ?? x.key] || "Unknown"
      ),
      datasets: [
        { label: "Leads", data: byStatusArr.map((x) => x.Count ?? x.count) },
      ],
    };

    const byTypeArr = (pick(s, "ByCustomerType", "byCustomerType") || [])
      .slice()
      .sort((a, b) => (a.Key ?? a.key) - (b.Key ?? b.key));
    const typeDoughnutData = {
      labels: byTypeArr.map(
        (x) => (x.Name ?? x.name) || TYPE_LABELS[x.Key ?? x.key] || "Unknown"
      ),
      datasets: [{ data: byTypeArr.map((x) => x.Count ?? x.count) }],
    };

    const fuArr = pick(s, "UpcomingFollowups", "upcomingFollowups") || [];
    const lineData = {
      labels: fuArr.map((x) => formatDateShort(x.Date ?? x.date)),
      datasets: [
        { label: "Follow-ups", data: fuArr.map((x) => x.Count ?? x.count) },
      ],
    };

    const commonOpts = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { position: "bottom" } },
    };

    return (
      <Row className="g-3 mt-1">
        <Col lg={6}>
          <Card className="shadow-sm rounded-3">
            <Card.Header className="fw-semibold">Status Breakdown</Card.Header>
            <Card.Body style={{ minHeight: 280 }}>
              <Bar data={statusBarData} options={commonOpts} />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="shadow-sm rounded-3">
            <Card.Header className="fw-semibold">Customer Type</Card.Header>
            <Card.Body style={{ minHeight: 280 }}>
              <Doughnut data={typeDoughnutData} options={commonOpts} />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={12}>
          <Card className="shadow-sm rounded-3">
            <Card.Header className="fw-semibold">
              Upcoming Follow-ups (next 14 days)
            </Card.Header>
            <Card.Body style={{ minHeight: 280 }}>
              <Line data={lineData} options={commonOpts} />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  renderAssigneeTable() {
    const s = this.state.summary || {};
    const rows = pick(s, "ByAssignee", "byAssignee") || [];

    return (
      <Card className="shadow-sm rounded-3 mt-3">
        <Card.Header className="fw-semibold">By Assignee</Card.Header>
        <Table hover responsive className="mb-0">
          <thead className="table-light">
            <tr>
              <th>Assignee</th>
              <th>Open</th>
              <th>Followup</th>
              <th>Closed</th>
              <th>Cancelled</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.userName ?? r.UserName ?? "Unassigned"}</td>
                <td>{r.open ?? r.Open ?? 0}</td>
                <td>{r.followup ?? r.Followup ?? 0}</td>
                <td>{r.closed ?? r.Closed ?? 0}</td>
                <td>{r.cancelled ?? r.Cancelled ?? 0}</td>
                <td className="fw-semibold">
                  {r.total ??
                    r.Total ??
                    (r.open ?? 0) +
                      (r.followup ?? 0) +
                      (r.closed ?? 0) +
                      (r.cancelled ?? 0)}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted">
                  No data
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>
    );
  }

  render() {
    const { loading, error } = this.state;
    const { show, onHide } = this.props;
    return (
      <Modal
        show={show}
        onHide={() => {
          onHide && onHide();
        }}
        size="xl"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Sale Leads — Analytics Dashboard</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: 300 }}>
          <div className="container-fluid py-3">
            {/* <h4 className="mb-3">Sales Lead Dashboard</h4> */}
            {this.renderFilters()}
            {error && <div className="alert alert-danger">{error}</div>}
            {loading && (
              <div className="text-center my-4">
                <Spinner />
              </div>
            )}
            {!loading && this.state.summary && (
              <>
                {this.renderKpis()}
                {this.renderCharts()}
                {this.renderAssigneeTable()}
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              this.props.refreshParent && this.props.refreshParent();
              this.props.onHide && this.props.onHide();
            }}
          >
            Close
          </Button>
          {/* <Button variant="primary" onClick={this.loadSummary}>
            Refresh Dashboard
          </Button> */}
        </Modal.Footer>
      </Modal>
    );
  }
}
